/* eslint-disable react-hooks/exhaustive-deps */
// External deps
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Button, ContentCard } from 'ypf-pas-general-ui-components';
import { BsPlusCircle, BsDashCircle } from 'react-icons/bs';
import { Row } from 'react-bootstrap';
import { FieldArray, useFormikContext } from 'formik';

import { stepEnum, stepEnumOfAccordion } from '../CreateServiceRequest/Constants';
import {
  RequestErrors,
  RequestField,
  RequestTouched,
  RequestValue
} from '../CreateServiceRequest/CreateServiceProps';
import ContractAlert from '../ContractAlert/ContractAlert';

import SelectProviderForm from './SelectProviderForm';
import useProviderForm from './useProviderForm';
import HeaderProviderForm from './HeaderProviderForm';

const disableAddProvider = (providers) =>
  providers?.some(
    (provider) =>
      provider?.provider === null ||
      provider?.provider === undefined ||
      provider?.contract === null ||
      provider?.contract === undefined ||
      provider?.notificate === null ||
      provider?.notificate === undefined ||
      provider?.businessUnit === null ||
      provider?.businessUnit === undefined
  );

const updateFieldValues = ({
  val,
  index,
  providerField,
  providerValue,
  requestField,
  requestValue,
  setFieldValue,
  setFieldTouched,
  resetField
}) => {
  if (val?.id !== providerValue?.providers[index]?.contract?.id) {
    setFieldValue(`${providerField}.providers[${index}].contract`, val);
    setFieldTouched(`${providerField}.providers[${index}].contract`);
    setFieldValue(`${providerField}.providers[${index}].alert`, null);

    resetField(
      providerValue?.providers[index]?.notificate,
      `${providerField}.providers[${index}].notificate`,
      null
    );
    resetField(
      requestValue?.associatedUsers?.approvers?.length,
      `${requestField}.associatedUsers.approvers`,
      []
    );
  }
};

const handleFetches = ({
  val,
  index,
  providerValue,
  fetchAlerts,
  fetchNotification,
  setFieldValue,
  providerField
}) => {
  if (val) {
    fetchAlerts(val.nombre, index);
    fetchNotification(providerValue?.providers[index].businessUnit.id, val.nombre, index);
  } else {
    setFieldValue(`${providerField}.providers[${index}].notificate`, null);
  }
};

const updateProviderFieldValues = ({
  val,
  index,
  providerField,
  providerValue,
  requestField,
  requestValue,
  setFieldValue,
  setFieldTouched,
  resetField,
  businessUnitSwitch
}) => {
  if (val?.id !== providerValue?.providers[index]?.provider?.id) {
    setFieldValue(`${providerField}.providers[${index}].provider`, val);
    if (val?.id !== providerValue?.providers[index]?.provider?.id && !businessUnitSwitch) {
      setFieldTouched(`${providerField}.providers[${index}].provider`);
    }
    resetField(
      requestValue?.associatedUsers?.receivers?.length,
      `${requestField}.associatedUsers.aditionalReceivers`,
      []
    );
  }
};

const handleProviderFetches = ({
  val,
  index,
  providerValue,
  requestValue,
  fetchContract,
  removeContractsFromProvider
}) => {
  if (val) {
    fetchContract(
      providerValue?.providers[index].businessUnit.id,
      requestValue.service.service.id,
      val.id,
      index
    );
  } else {
    removeContractsFromProvider(index);
  }
};

const resetProviderFields = (index, providerField, setFieldValue) => {
  setFieldValue(`${providerField}.providers[${index}].contract`, null);
  setFieldValue(`${providerField}.providers[${index}].notificate`, null);
  setFieldValue(`${providerField}.providers[${index}].alert`, null);
};

const ProviderServiceRequestContainer = ({
  resetField,
  params,
  requestErrors,
  requestField,
  requestTouched,
  requestValue,
  setStep,
  step,
  createSDS,
  isAutocomplete,
  disabledAllFields
}) => {
  const { id } = useParams();
  const [notificationArray, setNotificationArray] = useState(params.notificationOptions);

  const { values, setFieldValue, setFieldTouched, setErrors, validateForm } = useFormikContext();

  const providerValue = requestValue.provider;
  const providerField = `${requestField}.provider`;

  const {
    businessUnit,
    contractArray,
    businessUnitSwitch,
    setBusinessUnitSwitch,
    fetchProviders,
    fetchContract,
    removeContractsFromProvider,
    switchInputChange,
    fetchNotification,
    fetchAlerts
  } = useProviderForm({
    providerValue: providerValue?.providers,
    providerField: `${providerField}.providers`,
    setNotificationArray,
    requestValue,
    requestDetails: false,
    isAutocomplete
  });

  const businessUnitInvalid = (index) => {
    if (requestErrors?.provider?.providers && requestErrors?.provider?.provider?.length <= index) {
      return (
        requestErrors?.provider?.providers?.[index]?.businessUnit &&
        requestTouched?.provider?.providers?.[index]?.businessUnit
      );
    }

    return false;
  };

  const providerInvalid = (index) => {
    if (requestErrors?.provider?.providers && requestErrors?.provider?.provider?.length <= index) {
      return (
        requestErrors?.provider?.providers?.[index]?.provider &&
        requestTouched?.provider?.providers?.[index]?.provider
      );
    }

    return false;
  };

  const contractInvalid = (index) => {
    if (requestErrors?.provider?.providers && requestErrors?.provider?.provider?.length <= index) {
      return (
        requestErrors?.provider?.providers?.[index]?.contract &&
        requestTouched?.provider?.providers?.[index]?.contract
      );
    }

    return false;
  };

  const notificateInvalid = (index) => {
    if (requestErrors?.provider?.providers && requestErrors?.provider?.provider?.length <= index) {
      return (
        requestErrors?.provider?.providers?.[index]?.notificate &&
        requestTouched?.provider?.providers?.[index]?.notificate
      );
    }

    return false;
  };

  const containerRef = useRef();

  useEffect(() => {
    if (step === stepEnumOfAccordion.provider) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
    setFieldValue(`${requestField}.accordionStep`, stepEnumOfAccordion.provider);
  }, [step]);

  useEffect(() => {
    setNotificationArray(params.notificationOptions);
  }, [params.notificationOptions]);

  useEffect(() => {
    const othersBusinessUnits = providerValue?.providers?.some(
      (provider) => provider?.businessUnit?.id !== values?.ubication?.uunn?.id
    );

    if (!!id && othersBusinessUnits) setBusinessUnitSwitch(true);
  }, [id, providerValue?.providers]);

  return (
    <FieldArray name={`${providerField}.providers`}>
      {({ push }) => (
        <div ref={containerRef}>
          <ContentCard
            mark={
              values.currentStep >= stepEnum.services && step === stepEnumOfAccordion.provider
                ? 'primary'
                : 'none'
            }
            orientation="horizontal-vertical"
          >
            <>
              <HeaderProviderForm
                businessUnitSwitch={businessUnitSwitch}
                buttonAddProvider={{
                  disabled: disableAddProvider(providerValue?.providers) || disabledAllFields,
                  icon: BsPlusCircle,
                  onClick: () => {
                    push({
                      provider: '',
                      contract: null,
                      notificate: null,
                      businessUnit: values.ubication.uunn
                    });
                    setStep(stepEnumOfAccordion.provider);
                  }
                }}
                createSDS={createSDS}
                disabled={disabledAllFields}
                label="Solicitar a"
                onChangeSwitch={() => switchInputChange()}
              />
              <hr />
              {providerValue?.providers.map((providerForm, index) => (
                <Row className="mt-3" key={`${providerForm?.provider?.id}-provider`}>
                  <SelectProviderForm
                    businessUnitSwitch={businessUnitSwitch}
                    buttonRemove={{
                      disabled: providerValue?.providers.length <= 1 || disabledAllFields,
                      onClick: () => {
                        providerValue?.providers.splice(index, 1);
                        validateForm();
                        setStep(stepEnumOfAccordion.provider);
                        removeContractsFromProvider(index);
                      },
                      icon: BsDashCircle
                    }}
                    createSDS={createSDS}
                    reValidateAmount={() =>
                      // NOSONAR: with this prop we call the function that gets the total amount according to the contract.
                      fetchAlerts(
                        `${values?.accordions[index]?.provider?.providers[index]?.contract?.nombre}`,
                        index
                      )
                    }
                    selectContract={{
                      customLabel: providerValue?.providers[index]?.alert?.alert && (
                        <ContractAlert message={providerValue?.providers[index]?.alert?.message} />
                      ),
                      errors: requestErrors?.provider?.providers[index]?.contract,
                      isDisabled: !providerValue?.providers[index]?.provider || disabledAllFields,
                      isInvalid: contractInvalid(index),
                      name: `${providerField}.providers[${index}].contract`,
                      onChange: (val) => {
                        setFieldValue(
                          'changeDependencieApprover',
                          values.changeDependencieApprover + 1
                        );
                        updateFieldValues({
                          val,
                          index,
                          providerField,
                          providerValue,
                          requestField,
                          requestValue,
                          setFieldValue,
                          setFieldTouched,
                          resetField
                        });
                        handleFetches({
                          val,
                          index,
                          providerValue,
                          fetchAlerts,
                          fetchNotification,
                          setFieldValue,
                          providerField
                        });

                        setStep(stepEnumOfAccordion.provider);
                      },
                      options: contractArray?.filter(
                        (__, indexContract) => indexContract === index
                      )[0]?.contracts,
                      value: providerValue?.providers[index]?.contract,
                      availableAmount: providerValue?.providers[index]?.alert?.availableAmount
                    }}
                    selectNotificate={{
                      errors: requestErrors?.provider?.providers[index]?.notificate,
                      isDisabled:
                        !providerValue?.providers[index]?.provider ||
                        !providerValue?.providers[index]?.contract ||
                        disabledAllFields,
                      isInvalid: notificateInvalid(index),
                      name: `${providerField}.providers[${index}].notificate`,
                      onChange: (val) => {
                        setFieldValue(`${providerField}.providers[${index}].notificate`, val);
                        setFieldTouched(`${providerField}.providers[${index}].notificate`);
                        setStep(stepEnumOfAccordion.provider);
                      },
                      options: notificationArray,
                      value: providerValue?.providers[index]?.notificate
                    }}
                    selectProvider={{
                      errors: requestErrors?.provider?.providers[index]?.provider,
                      fetchData: (value) =>
                        fetchProviders(
                          value,
                          providerValue.providers[index].businessUnit.id,
                          requestValue.service.service.id
                        ),

                      isDisabled:
                        !providerValue?.providers[index]?.businessUnit || disabledAllFields,
                      isInvalid: providerInvalid(index),
                      name: `${providerField}.providers[${index}].provider`,
                      onChange: (val) => {
                        updateProviderFieldValues({
                          val,
                          index,
                          providerField,
                          providerValue,
                          requestField,
                          requestValue,
                          setFieldValue,
                          setFieldTouched,
                          resetField,
                          businessUnitSwitch
                        });

                        handleProviderFetches({
                          val,
                          index,
                          providerValue,
                          requestValue,
                          fetchContract,
                          removeContractsFromProvider
                        });

                        resetProviderFields(index, providerField, setFieldValue);

                        setStep(stepEnumOfAccordion.provider);
                      },
                      value: providerValue?.providers[index]?.provider
                    }}
                    selectUunn={{
                      errors: requestErrors?.provider?.providers[index]?.businessUnit,
                      isDisabled: !businessUnitSwitch || disabledAllFields,
                      isInvalid: businessUnitInvalid(index),
                      name: `${providerField}.providers[${index}].businessUnit`,
                      onChange: (val) => {
                        if (val?.id !== providerValue?.providers[index]?.businessUnit?.id) {
                          setFieldValue(`${providerField}.providers[${index}].businessUnit`, val);
                          setFieldValue(`${requestField}.associatedUsers.approvers`, []);
                        }

                        setFieldValue(`${providerField}.providers[${index}].provider`, null);
                        setFieldValue(`${providerField}.providers[${index}].contract`, null);
                        setFieldValue(`${providerField}.providers[${index}].notificate`, null);
                        setFieldValue(`${providerField}.providers[${index}].alert`, null);

                        setStep(stepEnumOfAccordion.provider);
                      },
                      options: businessUnit,
                      value: providerValue?.providers[index]?.businessUnit
                    }}
                  />
                </Row>
              ))}
              {step === 3 && (
                <>
                  <hr />
                  <div className="gap row justify-content-end">
                    <Button
                      label="Cancelar"
                      onClick={() => {
                        setFieldValue(`${providerField}.providers`, []);
                        setErrors(providerField, {});
                        setStep(stepEnumOfAccordion.service);
                      }}
                      type="button"
                      variant="outline-primary"
                    />
                    <Button
                      disabled={requestErrors?.provider?.providers?.length > 0}
                      label="Continuar"
                      onClick={() => setStep(stepEnumOfAccordion.associatedUsers)}
                      type="button"
                      variant="primary"
                    />
                  </div>
                </>
              )}
            </>
          </ContentCard>
        </div>
      )}
    </FieldArray>
  );
};

ProviderServiceRequestContainer.propTypes = {
  resetField: PropTypes.func,
  step: PropTypes.number.isRequired,
  setStep: PropTypes.func.isRequired,
  params: PropTypes.shape({
    notificationOptions: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  requestErrors: RequestErrors,
  requestField: RequestField.isRequired,
  requestTouched: RequestTouched,
  requestValue: RequestValue,
  disabledAllFields: PropTypes.bool,
  createSDS: PropTypes.bool,
  isAutocomplete: PropTypes.bool
};

ProviderServiceRequestContainer.defaultProps = {
  resetField: () => {
    /* Empty */
  },
  params: {
    notificationOptions: []
  },
  requestTouched: {},
  requestErrors: {},
  requestValue: {},
  disabledAllFields: false,
  createSDS: false,
  isAutocomplete: false
};

export default ProviderServiceRequestContainer;
